import { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  PiggyBank,
  Settings,
  User,
  CreditCard,
  Shield,
  Search,
  History,
  Wallet,
  Tags,
} from 'lucide-react';
import {
  IconDashboard, IconShared, IconPersonal, IconEnvelope, IconStats, IconSettings,
  IconShield, IconChevron, IconPin, IconSun, IconMoon, IconMonitor, IconLogout, IconMenu,
} from '../../config/meridianIcons';
import { TOPBAR_SLOT_ID } from './TopbarPortal';
import AnnouncementBanner from '../common/AnnouncementBanner';
import NotificationsMenu from '../common/NotificationsMenu';
import { useAnnouncementStore } from '../../store/announcementStore';
import { getIcon, resolveNavIcon } from '../../config/iconSets';
import type { IconSet } from '../../config/iconSets';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useAuthStore } from '../../store/authStore';
import { useLivingType, useSettingsStore } from '../../store/settingsStore';
import { useTutorialStore, getTutorialSteps } from '../../store/tutorialStore';
import { useLivingTerms } from '../../hooks/useLivingTerms';
import { useBankAccountStore } from '../../store/bankAccountStore';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { useThemeStore } from '../../store/themeStore';
import { logout as logoutApi } from '../../services/auth';
import api from '../../services/api';
import { clsx } from 'clsx';
import { useInactivityTimeout } from '../../hooks/useInactivityTimeout';
import InactivityWarningModal from '../common/InactivityWarningModal';
import TutorialOverlay from '../common/TutorialOverlay';
import LanguageSwitcher from '../common/LanguageSwitcher';
import BrandMark from '../common/BrandMark';
import { getNavItemClasses, getSubItemClasses, getSidebarClasses, getHeaderClasses, getDensityClasses } from '../../config/themeHelpers';
import type { AccountType } from '../../types';

interface NavItem {
  nameKey: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  hideForAccountTypes?: AccountType[];
  tutorialId?: string;
  adminOnly?: boolean;
}

// Sous-items du menu « Profil » (libellés déjà traduits, cf. profileSubItems)
interface SubNavItem {
  key: string;
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  tutorialId?: string;
  /** Trait de séparation affiché au-dessus de cet item (profil ↔ réglages) */
  separatorBefore?: boolean;
  /** Pastille de couleur (sous-items « compte bancaire » du rail) */
  color?: string;
  /** Compte hors plan : affiché mais non navigable */
  locked?: boolean;
}

const PROFILE_HREF = '/dashboard/profil';
const SETTINGS_HREF = '/dashboard/reglages';

// ═══════════════════════════════════════════════════════════
// TopnavLayout — responsive priority+ navigation
// Measures item widths once, then uses pure math on resize.
// On small screens: hamburger opens a dropdown (not a sidebar drawer)
// to stay consistent with the topnav paradigm.
// ═══════════════════════════════════════════════════════════
function TopnavLayout({
  filteredNavigation, iconSet, location, headerStyles, densityStyles,
  moreMenuOpen, setMoreMenuOpen, handleLogout,
  renderUserMenu, renderModals, t,
  activeProfileKey, profileFlyoutOpen, setProfileFlyoutOpen, renderProfileSubItems,
}: {
  filteredNavigation: NavItem[];
  iconSet: IconSet;
  location: ReturnType<typeof useLocation>;
  headerStyles: ReturnType<typeof getHeaderClasses>;
  densityStyles: ReturnType<typeof getDensityClasses>;
  moreMenuOpen: boolean;
  setMoreMenuOpen: (v: boolean) => void;
  handleLogout: () => void;
  renderUserMenu: () => React.ReactNode;
  renderModals: () => React.ReactNode;
  t: TFunction;
  activeProfileKey: string | null;
  profileFlyoutOpen: boolean;
  setProfileFlyoutOpen: (v: boolean) => void;
  renderProfileSubItems: (onNavigate?: () => void, indented?: boolean) => React.ReactNode;
}) {
  const navContainerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const itemWidthsRef = useRef<number[]>([]);
  const moreButtonWidthRef = useRef(0);
  const [visibleCount, setVisibleCount] = useState(filteredNavigation.length);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const MenuIcon = getIcon(iconSet, 'menu');
  const CloseIcon = getIcon(iconSet, 'close');
  const MoreHorizontalIcon = getIcon(iconSet, 'moreHorizontal');
  const LogOutIcon = getIcon(iconSet, 'logOut');
  const ChevronDownIcon = getIcon(iconSet, 'chevronDown');

  // Step 1: measure natural widths of all items via a hidden ruler div
  const measureWidths = useCallback(() => {
    const ruler = measureRef.current;
    if (!ruler) return;
    const items = ruler.querySelectorAll<HTMLElement>('[data-measure-item]');
    const widths: number[] = [];
    items.forEach((item) => widths.push(item.getBoundingClientRect().width));
    itemWidthsRef.current = widths;

    const moreBtn = ruler.querySelector<HTMLElement>('[data-measure-more]');
    if (moreBtn) moreButtonWidthRef.current = moreBtn.getBoundingClientRect().width;
  }, []);

  // Step 2: calculate how many items fit using cached widths
  const recalculate = useCallback(() => {
    const container = navContainerRef.current;
    if (!container) return;
    const widths = itemWidthsRef.current;
    if (widths.length === 0) return;

    const available = container.getBoundingClientRect().width;
    const gap = 4; // gap-1 = 0.25rem = 4px
    const moreW = moreButtonWidthRef.current + gap;

    // First check: do ALL items fit without "Plus"?
    let total = 0;
    let allFit = true;
    for (let i = 0; i < widths.length; i++) {
      total += widths[i] + (i > 0 ? gap : 0);
      if (total > available) { allFit = false; break; }
    }
    if (allFit) { setVisibleCount(widths.length); return; }

    // Not all fit → calculate how many fit WITH "Plus" button reserved
    total = 0;
    let count = 0;
    for (let i = 0; i < widths.length; i++) {
      const needed = total + widths[i] + (i > 0 ? gap : 0) + moreW;
      if (needed <= available) {
        total += widths[i] + (i > 0 ? gap : 0);
        count++;
      } else {
        break;
      }
    }
    setVisibleCount(count);
  }, []);

  // Measure once on mount + when nav items or translations change
  useEffect(() => {
    // Wait one frame for the hidden ruler to render
    requestAnimationFrame(() => {
      measureWidths();
      recalculate();
    });
  }, [filteredNavigation, t, measureWidths, recalculate]);

  // Observe container resize
  useEffect(() => {
    const container = navContainerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => recalculate());
    observer.observe(container);
    return () => observer.disconnect();
  }, [recalculate]);

  const overflowNav = filteredNavigation.slice(visibleCount);

  // Shared: render a single nav link for dropdown menus
  // Profil est rendu comme un groupe : en-tête + ses sous-items indentés
  const renderDropdownItem = (item: NavItem, onClick?: () => void) => {
    const Icon = resolveNavIcon(iconSet, item.icon as React.ComponentType<unknown>);

    if (item.href === PROFILE_HREF) {
      return (
        <div key={item.href} {...(item.tutorialId && { 'data-tutorial': item.tutorialId })}>
          <div className="flex items-center gap-3 px-4 py-2.5 text-sm font-medium text-gray-400 uppercase tracking-wide text-xs">
            <Icon className="w-4 h-4" />
            {t(item.nameKey)}
          </div>
          {renderProfileSubItems(onClick, true)}
        </div>
      );
    }

    return (
      <NavLink
        key={item.href}
        to={item.href}
        onClick={onClick}
        className={({ isActive }) => clsx(
          'flex items-center gap-3 px-4 py-2.5 text-sm transition-colors',
          isActive ? 'text-primary-700 bg-primary-50/50' : 'text-gray-600 hover:bg-gray-50'
        )}
      >
        <Icon className="w-4 h-4" />
        {t(item.nameKey)}
      </NavLink>
    );
  };

  return (
    <div className="min-h-screen page-bg" style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* Hidden ruler — renders all items off-screen to measure natural widths */}
      <div
        ref={measureRef}
        aria-hidden
        className="fixed top-0 left-0 flex items-center gap-1 pointer-events-none opacity-0 -z-10 whitespace-nowrap"
      >
        {filteredNavigation.map((item) => {
          const Icon = resolveNavIcon(iconSet, item.icon as React.ComponentType<unknown>);
          return (
            <span key={item.href} data-measure-item className="flex items-center gap-2 px-4 py-2 text-sm font-medium">
              <Icon className="w-4 h-4" />
              {t(item.nameKey)}
              {item.href === PROFILE_HREF && <ChevronDownIcon className="w-3.5 h-3.5" />}
            </span>
          );
        })}
        <span data-measure-more className="flex items-center gap-1.5 px-3 py-2 text-sm">
          <MoreHorizontalIcon className="w-4 h-4" />
          {t('common:more', 'Plus')}
        </span>
      </div>

      <header className={clsx('sticky top-0 z-40', headerStyles.container)}>
        <div className="flex items-center h-16 px-4 lg:px-8 max-w-[1400px] mx-auto w-full">
          {/* Mobile: hamburger → opens full dropdown below header */}
          <button
            onClick={() => { setMobileMenuOpen(!mobileMenuOpen); setMoreMenuOpen(false); }}
            className={clsx(headerStyles.menuButtonClasses, 'lg:hidden mr-3')}
          >
            {mobileMenuOpen ? <CloseIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
          </button>

          {/* Brand */}
          <span className="text-xl font-bold text-primary-600 mr-8 shrink-0">
            Splinity
          </span>

          {/* Desktop: responsive horizontal navigation */}
          <nav ref={navContainerRef} className="hidden lg:flex items-center gap-1 flex-1 min-w-0">
            {filteredNavigation.map((item, index) => {
              const isProfile = item.href === PROFILE_HREF;
              const isActive = isProfile
                ? activeProfileKey !== null
                : item.href === '/dashboard'
                  ? location.pathname === '/dashboard'
                  : location.pathname.startsWith(item.href);
              const ItemIcon = resolveNavIcon(iconSet, item.icon as React.ComponentType<unknown>);
              const isVisible = index < visibleCount;

              // Profil : bouton avec menu déroulant de ses sous-items
              if (isProfile) {
                return (
                  <div
                    key={item.href}
                    className={clsx('relative shrink-0', !isVisible && 'hidden')}
                    {...(item.tutorialId && { 'data-tutorial': item.tutorialId })}
                  >
                    <button
                      onClick={() => setProfileFlyoutOpen(!profileFlyoutOpen)}
                      tabIndex={isVisible ? 0 : -1}
                      className={clsx(
                        'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-150 whitespace-nowrap',
                        isActive
                          ? 'text-primary-700 bg-white/60 backdrop-blur-sm shadow-sm rounded-xl'
                          : 'text-gray-500 hover:text-gray-800 hover:bg-white/30 rounded-xl'
                      )}
                    >
                      <ItemIcon className="w-4 h-4" />
                      {t(item.nameKey)}
                      <ChevronDownIcon className={clsx('w-3.5 h-3.5 transition-transform', profileFlyoutOpen && 'rotate-180')} />
                    </button>
                    {profileFlyoutOpen && (
                      <>
                        <div className="fixed inset-0 z-10" onClick={() => setProfileFlyoutOpen(false)} />
                        <div className="absolute left-0 z-20 mt-2 w-56 bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 py-2">
                          {renderProfileSubItems(() => setProfileFlyoutOpen(false))}
                        </div>
                      </>
                    )}
                  </div>
                );
              }

              return (
                <NavLink
                  key={item.href}
                  to={item.href}
                  end={item.href === '/dashboard'}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all duration-150 whitespace-nowrap shrink-0',
                    isActive
                      ? 'text-primary-700 bg-white/60 backdrop-blur-sm shadow-sm rounded-xl'
                      : 'text-gray-500 hover:text-gray-800 hover:bg-white/30 rounded-xl',
                    !isVisible && 'hidden'
                  )}
                  tabIndex={isVisible ? 0 : -1}
                  {...(item.tutorialId && { 'data-tutorial': item.tutorialId })}
                >
                  <ItemIcon className="w-4 h-4" />
                  {t(item.nameKey)}
                </NavLink>
              );
            })}

            {/* "Plus" overflow menu */}
            {overflowNav.length > 0 && (
              <div className="relative shrink-0">
                <button
                  onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-800 hover:bg-white/30 rounded-xl transition-all whitespace-nowrap"
                >
                  <MoreHorizontalIcon className="w-4 h-4" />
                  <span>{t('common:more', 'Plus')}</span>
                </button>
                {moreMenuOpen && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setMoreMenuOpen(false)} />
                    <div className="absolute right-0 z-20 mt-2 w-52 bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 py-2">
                      {overflowNav.map((item) => renderDropdownItem(item, () => setMoreMenuOpen(false)))}
                      <div className="border-t my-1 mx-3 border-gray-200/50" />
                      <button onClick={handleLogout} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50">
                        <LogOutIcon className="w-4 h-4" />{t('logout')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </nav>

          <div className="flex-1 lg:hidden" />
          {renderUserMenu()}
        </div>

        {/* Mobile dropdown menu — slides down from header, consistent with topnav */}
        {mobileMenuOpen && (
          <>
            <div className="fixed inset-0 z-10 lg:hidden" onClick={() => setMobileMenuOpen(false)} />
            <div className="relative z-20 lg:hidden bg-white/95 backdrop-blur-xl border-t border-gray-200/50 shadow-lg">
              <div className="max-w-[1400px] mx-auto py-2 px-2">
                {filteredNavigation.map((item) => renderDropdownItem(item, () => setMobileMenuOpen(false)))}
                <div className="border-t my-1 mx-3 border-gray-200/50" />
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50">
                  <LogOutIcon className="w-4 h-4" />{t('logout')}
                </button>
              </div>
            </div>
          </>
        )}
      </header>

      <main className={clsx('max-w-[1400px] mx-auto', densityStyles.mainPadding, densityStyles.mainPaddingDesktop)}>
        <AnnouncementBanner />
        <Outlet />
      </main>

      {renderModals()}
    </div>
  );
}

const navigation: NavItem[] = [
  { nameKey: 'nav.dashboard', href: '/dashboard', icon: LayoutDashboard },
  { nameKey: 'nav.sharedAccounts', href: '/dashboard/compte-commun', icon: Users, hideForAccountTypes: ['personal_with_transfers'], tutorialId: 'nav-compte-commun' },
  { nameKey: 'nav.myBudget', href: '/dashboard/mon-budget', icon: PiggyBank, hideForAccountTypes: ['joint_only'], tutorialId: 'nav-mon-budget' },
  { nameKey: 'nav.search', href: '/dashboard/recherche', icon: Search },
  { nameKey: 'nav.profile', href: PROFILE_HREF, icon: User, tutorialId: 'nav-profil' },
  { nameKey: 'nav.subscription', href: '/dashboard/abonnement', icon: CreditCard },
  { nameKey: 'nav.administration', href: '/dashboard/admin', icon: Shield, adminOnly: true },
];

export default function DashboardLayout() {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [budgetMenuOpen, setBudgetMenuOpen] = useState(false);
  const [sharedMenuOpen, setSharedMenuOpen] = useState(false);
  // Accordéon « Profil » dans la sidebar / drawer
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  // Menu déroulant « Profil » pour les layouts topnav et rail
  const [profileFlyoutOpen, setProfileFlyoutOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  // ─── Rail Méridien : ancrage (persisté), survol-expansion, groupe ouvert ───
  const [railPinned, setRailPinned] = useState<boolean>(() => {
    try { return localStorage.getItem('splinity-rail-pinned') === 'true'; } catch { return false; }
  });
  // L'expansion au survol/focus est 100 % CSS (cf. `.rail:hover .rail-inner`) :
  // aucun état React, donc aucun re-rendu sur un simple survol.
  const [railOpenGroup, setRailOpenGroup] = useState<string | null>(null);
  const toggleRailPin = useCallback(() => {
    setRailPinned((prev) => {
      const next = !prev;
      try { localStorage.setItem('splinity-rail-pinned', String(next)); } catch { /* ignore */ }
      return next;
    });
  }, []);
  const { bankAccounts, sharedBankAccounts, fetchBankAccounts, fetchSharedBankAccounts } = useBankAccountStore();
  const navigate = useNavigate();
  const location = useLocation();

  const { user, logout, refreshToken, setUser } = useAuthStore();
  const { settings, fetchSettings } = useSettingsStore();
  const { isNewUser, startTutorial, setNewUser, isActive: tutorialIsActive, currentStepIndex } = useTutorialStore();
  const { planInfo, fetchPlanInfo } = useSubscriptionStore();
  const fetchAnnouncement = useAnnouncementStore((s) => s.fetchAnnouncement);
  const layout = useThemeStore((s) => s.theme.layout);
  const mode = useThemeStore((s) => s.mode);
  const setMode = useThemeStore((s) => s.setMode);
  const iconSet: IconSet = layout.iconSet || 'lucide';
  const livingType = useLivingType();
  const terms = useLivingTerms(livingType);

  // L'annonce est chargée une fois à l'entrée dans l'espace connecté : elle
  // change rarement, et la recharger à chaque navigation n'apporterait rien.
  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  const sidebarStyles = getSidebarClasses(layout);
  const headerStyles = getHeaderClasses(layout);
  const densityStyles = getDensityClasses(layout.density);
  const isColored = layout.sidebarStyle === 'colored';
  const isFloating = layout.sidebarShape === 'floating';

  // Resolve themed icons
  const MenuIcon = getIcon(iconSet, 'menu');
  const CloseIcon = getIcon(iconSet, 'close');
  const LogOutIcon = getIcon(iconSet, 'logOut');
  const ChevronDownIcon = getIcon(iconSet, 'chevronDown');
  const ChevronRightIcon = getIcon(iconSet, 'chevronRight');
  const LockIcon = getIcon(iconSet, 'lock');
  const UserIcon = getIcon(iconSet, 'user');

  const {
    showWarning: showInactivityWarning,
    secondsRemaining,
    extendSession,
    logout: inactivityLogout,
  } = useInactivityTimeout({
    onLogout: () => navigate('/'),
  });

  useEffect(() => {
    const refreshProfile = async () => {
      try {
        const response = await api.get('/user/profile');
        if (response.data.success && response.data.data) {
          setUser(response.data.data);
        }
      } catch {
        // Ignorer
      }
    };
    refreshProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);
  useEffect(() => { fetchPlanInfo(); }, [fetchPlanInfo]);
  useEffect(() => { fetchBankAccounts(); }, [fetchBankAccounts]);
  useEffect(() => {
    if (!user?.coupleId) return;
    fetchSharedBankAccounts();
  }, [user?.coupleId, fetchSharedBankAccounts]);

  useEffect(() => {
    if (location.pathname.startsWith('/dashboard/mon-budget')) setBudgetMenuOpen(true);
    if (location.pathname.startsWith('/dashboard/compte-commun')) setSharedMenuOpen(true);
    if (location.pathname.startsWith(PROFILE_HREF) || location.pathname.startsWith(SETTINGS_HREF)) setProfileMenuOpen(true);
    // Rail : auto-ouvre le groupe de la rubrique courante
    if (location.pathname.startsWith('/dashboard/mon-budget')) setRailOpenGroup('/dashboard/mon-budget');
    else if (location.pathname.startsWith('/dashboard/compte-commun')) setRailOpenGroup('/dashboard/compte-commun');
    else if (location.pathname.startsWith(PROFILE_HREF) || location.pathname.startsWith(SETTINGS_HREF)) setRailOpenGroup(PROFILE_HREF);
  }, [location.pathname]);

  // Étapes du tutoriel qui pointent le menu : il faut que la cible soit atteignable
  // — sous-menu « Réglages » déplié, et tiroir ouvert au format tactile où le rail
  // n'est pas affiché.
  useEffect(() => {
    if (!tutorialIsActive) return;
    const stepId = getTutorialSteps(settings?.accountType)[currentStepIndex]?.id;
    if (!stepId) return;

    if (stepId === 'nav-reglages') {
      setProfileMenuOpen(true);
      setProfileFlyoutOpen(true);
      setRailOpenGroup(PROFILE_HREF);
    }

    const pointsAtMenu = stepId === 'navigation' || stepId.startsWith('nav-');
    if (window.innerWidth < 1024) setSidebarOpen(pointsAtMenu);
  }, [tutorialIsActive, currentStepIndex, settings?.accountType]);

  useEffect(() => {
    if (isNewUser && location.pathname === '/dashboard') {
      const timer = setTimeout(() => { startTutorial(); setNewUser(false); }, 500);
      return () => clearTimeout(timer);
    }
  }, [isNewUser, location.pathname, startTutorial, setNewUser]);

  // Tiroir mobile ouvert : Échap ferme, le fond ne défile plus derrière, et le
  // retour au desktop (le tiroir y est masqué en CSS) le referme — sinon le
  // verrou de défilement survivrait à un simple redimensionnement.
  useEffect(() => {
    if (!sidebarOpen) return;
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') setSidebarOpen(false); };
    const desktop = window.matchMedia('(min-width: 1024px)');
    const onDesktopChange = () => { if (desktop.matches) setSidebarOpen(false); };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    desktop.addEventListener('change', onDesktopChange);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      desktop.removeEventListener('change', onDesktopChange);
      document.body.style.overflow = previousOverflow;
    };
  }, [sidebarOpen]);

  const defaultBankAccount = useMemo(() => bankAccounts.find(acc => acc.isDefault) || bankAccounts[0], [bankAccounts]);
  const defaultSharedAccount = useMemo(() => sharedBankAccounts.find(acc => acc.isDefault) || sharedBankAccounts[0], [sharedBankAccounts]);

  const accessibleBankAccountIds = useMemo(() => {
    if (!planInfo || planInfo.limits.personalAccounts === -1) return new Set(bankAccounts.map(a => a.id));
    const limit = planInfo.limits.personalAccounts;
    const sorted = [...bankAccounts].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    return new Set(sorted.slice(0, limit).map(a => a.id));
  }, [bankAccounts, planInfo]);

  const accessibleSharedAccountIds = useMemo(() => {
    if (!planInfo || planInfo.limits.sharedAccounts === -1) return new Set(sharedBankAccounts.map(a => a.id));
    const limit = planInfo.limits.sharedAccounts;
    const sorted = [...sharedBankAccounts].sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0));
    return new Set(sorted.slice(0, limit).map(a => a.id));
  }, [sharedBankAccounts, planInfo]);

  const filteredNavigation = useMemo(() => {
    const accountType = settings?.accountType ?? 'joint_with_personal';
    const isAdmin = user?.role === 'admin';
    return navigation.filter(item => {
      if (item.adminOnly && !isAdmin) return false;
      if (item.hideForAccountTypes && item.hideForAccountTypes.includes(accountType)) return false;
      return true;
    });
  }, [settings?.accountType, user?.role]);

  // ═══════════════════════════════════════════════════════════
  // Sous-menu « Profil » : onglets de la page Profil (via ?tab=) + Réglages.
  // Le profil couple n'a pas d'entrée : il fait partie des réglages du foyer.
  // ═══════════════════════════════════════════════════════════
  const profileSubItems = useMemo<SubNavItem[]>(() => {
    const accountType = settings?.accountType ?? 'joint_with_personal';
    const items: SubNavItem[] = [
      { key: 'personal', label: t('profile:tabs.personal'), href: `${PROFILE_HREF}?tab=personal`, icon: User },
    ];
    if (user?.coupleId) {
      items.push({ key: 'activity', label: t('profile:tabs.activity'), href: `${PROFILE_HREF}?tab=activity`, icon: History });
    }
    // Réglages : trois pages distinctes, séparées visuellement du bloc profil
    items.push({
      key: 'settings-shared',
      // « Réglages couple » / « Réglages colocation » — « communs » se lisait
      // comme « courants » et non comme « du foyer ».
      label: terms.settingsLabel,
      href: `${SETTINGS_HREF}?tab=shared`,
      icon: Settings,
      tutorialId: 'nav-reglages',
      separatorBefore: true,
    });
    // Les réglages personnels n'existent pas en mode « compte commun uniquement »
    if (accountType !== 'joint_only') {
      items.push({ key: 'settings-personal', label: t('settings:sections.personal'), href: `${SETTINGS_HREF}?tab=personal`, icon: Wallet });
    }
    items.push({ key: 'settings-categories', label: t('settings:sections.categoriesManagement'), href: `${SETTINGS_HREF}?tab=categories`, icon: Tags });
    return items;
  }, [t, terms.settingsLabel, user?.coupleId, settings?.accountType]);

  // Sous-item actif (null si on n'est ni sur Profil ni sur Réglages)
  const activeProfileKey = useMemo(() => {
    const tab = new URLSearchParams(location.search).get('tab');
    if (location.pathname.startsWith(SETTINGS_HREF)) {
      const accountType = settings?.accountType ?? 'joint_with_personal';
      if (tab === 'categories') return 'settings-categories';
      if (tab === 'personal' && accountType !== 'joint_only') return 'settings-personal';
      return 'settings-shared';
    }
    if (!location.pathname.startsWith(PROFILE_HREF)) return null;
    if (tab === 'activity' && user?.coupleId) return tab;
    return 'personal';
  }, [location.pathname, location.search, user?.coupleId, settings?.accountType]);

  // Fil d'Ariane du layout rail : rubrique courante + sous-rubrique éventuelle
  const breadcrumbLabels = useMemo(() => {
    const isCurrent = (item: NavItem) => {
      if (item.href === PROFILE_HREF) return activeProfileKey !== null;
      if (item.href === '/dashboard') return location.pathname === '/dashboard';
      return location.pathname.startsWith(item.href);
    };
    const parent = filteredNavigation.find(isCurrent);
    if (!parent) return [];
    // Dans le rail, la rubrique « Profil » s'affiche « Réglages » (le profil
    // personnel est sur l'avatar) : le fil d'Ariane suit le même libellé.
    const labels = [
      parent.href === PROFILE_HREF && activeProfileKey !== 'personal' ? t('nav.settings') : t(parent.nameKey),
    ];
    if (parent.href === PROFILE_HREF) {
      const sub = profileSubItems.find(s => s.key === activeProfileKey);
      if (sub) labels.push(sub.label);
    }
    return labels;
  }, [filteredNavigation, location.pathname, activeProfileKey, profileSubItems, t]);

  const handleLogout = async () => {
    try { if (user?.id) await logoutApi(user.id, refreshToken || undefined); }
    catch { /* Ignorer */ }
    finally { logout(); navigate('/'); }
  };

  // ═══════════════════════════════════════════════════════════
  // Shared: full sidebar nav items (used by sidebar + mobile drawer)
  // ═══════════════════════════════════════════════════════════
  const renderNavItem = (item: NavItem, closeSidebar?: () => void) => {
    const ThemedIcon = resolveNavIcon(iconSet, item.icon as React.ComponentType<unknown>);

    if (item.href === '/dashboard/mon-budget' && bankAccounts.length > 0) {
      const isActive = location.pathname.startsWith('/dashboard/mon-budget');
      return (
        <div key={item.href} data-tutorial={item.tutorialId}>
          <div className={getNavItemClasses(layout, isActive, isColored)}>
            <ThemedIcon className="w-5 h-5" />
            <span className="flex-1" onClick={() => { navigate(defaultBankAccount ? `/dashboard/mon-budget?bankAccountId=${defaultBankAccount.id}` : '/dashboard/mon-budget'); setBudgetMenuOpen(true); closeSidebar?.(); }}>
              {t(item.nameKey)}
            </span>
            <button onClick={(e) => { e.stopPropagation(); setBudgetMenuOpen(!budgetMenuOpen); }} className={clsx('p-1 rounded', isColored ? 'hover:bg-white/10' : 'hover:bg-gray-200')}>
              <ChevronRightIcon className={clsx('w-4 h-4 transition-transform', budgetMenuOpen && 'rotate-90')} />
            </button>
          </div>
          {budgetMenuOpen && (
            <div className="ml-6 mt-1 space-y-1">
              {bankAccounts.map((account) => {
                const isLocked = !accessibleBankAccountIds.has(account.id);
                const accountUrl = `/dashboard/mon-budget?bankAccountId=${account.id}`;
                const isAccountActive = !isLocked && location.pathname === '/dashboard/mon-budget' && location.search === `?bankAccountId=${account.id}`;
                return (
                  <button key={account.id} onClick={() => { if (!isLocked) { navigate(accountUrl); closeSidebar?.(); } }} title={isLocked ? t('nav.accountLockedPersonal') : undefined} className={getSubItemClasses(layout, isAccountActive, isLocked, isColored)}>
                    <span className={clsx('w-3 h-3 rounded-full', isLocked && 'opacity-50')} style={{ backgroundColor: account.color }} />
                    {account.name}
                    {isLocked && <LockIcon className="w-3 h-3 ml-auto text-gray-400" />}
                    {!isLocked && account.isDefault && <span className={clsx('text-xs', isColored ? 'text-white/40' : 'text-gray-400')}>{t('default')}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (item.href === '/dashboard/compte-commun' && sharedBankAccounts.length > 0) {
      const isActive = location.pathname.startsWith('/dashboard/compte-commun');
      return (
        <div key={item.href} data-tutorial={item.tutorialId}>
          <div className={getNavItemClasses(layout, isActive, isColored)}>
            <ThemedIcon className="w-5 h-5" />
            <span className="flex-1" onClick={() => { navigate(defaultSharedAccount ? `/dashboard/compte-commun?sharedBankAccountId=${defaultSharedAccount.id}` : '/dashboard/compte-commun'); setSharedMenuOpen(true); closeSidebar?.(); }}>
              {t(item.nameKey)}
            </span>
            <button onClick={(e) => { e.stopPropagation(); setSharedMenuOpen(!sharedMenuOpen); }} className={clsx('p-1 rounded', isColored ? 'hover:bg-white/10' : 'hover:bg-gray-200')}>
              <ChevronRightIcon className={clsx('w-4 h-4 transition-transform', sharedMenuOpen && 'rotate-90')} />
            </button>
          </div>
          {sharedMenuOpen && (
            <div className="ml-6 mt-1 space-y-1">
              {sharedBankAccounts.map((account) => {
                const isLocked = !accessibleSharedAccountIds.has(account.id);
                const accountUrl = `/dashboard/compte-commun?sharedBankAccountId=${account.id}`;
                const isAccountActive = !isLocked && location.pathname === '/dashboard/compte-commun' && location.search === `?sharedBankAccountId=${account.id}`;
                return (
                  <button key={account.id} onClick={() => { if (!isLocked) { navigate(accountUrl); closeSidebar?.(); } }} title={isLocked ? t('nav.accountLockedShared') : undefined} className={getSubItemClasses(layout, isAccountActive, isLocked, isColored)}>
                    <span className={clsx('w-3 h-3 rounded-full', isLocked && 'opacity-50')} style={{ backgroundColor: account.color }} />
                    {account.name}
                    {isLocked && <LockIcon className="w-3 h-3 ml-auto text-gray-400" />}
                    {!isLocked && account.isDefault && <span className={clsx('text-xs', isColored ? 'text-white/40' : 'text-gray-400')}>{t('default')}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    if (item.href === PROFILE_HREF) {
      const isActive = activeProfileKey !== null;
      return (
        <div key={item.href} data-tutorial={item.tutorialId}>
          <div className={getNavItemClasses(layout, isActive, isColored)}>
            <ThemedIcon className="w-5 h-5" />
            <span className="flex-1 cursor-pointer" onClick={() => { navigate(`${PROFILE_HREF}?tab=personal`); setProfileMenuOpen(true); closeSidebar?.(); }}>
              {t(item.nameKey)}
            </span>
            <button onClick={(e) => { e.stopPropagation(); setProfileMenuOpen(!profileMenuOpen); }} className={clsx('p-1 rounded', isColored ? 'hover:bg-white/10' : 'hover:bg-gray-200')}>
              <ChevronRightIcon className={clsx('w-4 h-4 transition-transform', profileMenuOpen && 'rotate-90')} />
            </button>
          </div>
          {profileMenuOpen && (
            <div className="ml-6 mt-1 space-y-1">
              {profileSubItems.map((sub) => {
                const SubIcon = resolveNavIcon(iconSet, sub.icon as React.ComponentType<unknown>);
                return (
                  <div key={sub.key}>
                    {sub.separatorBefore && (
                      <div className={clsx('my-2 border-t', isColored ? 'border-white/10' : 'border-gray-200')} />
                    )}
                    <button
                      onClick={() => { navigate(sub.href); closeSidebar?.(); }}
                      className={getSubItemClasses(layout, activeProfileKey === sub.key, false, isColored)}
                      {...(sub.tutorialId && { 'data-tutorial': sub.tutorialId })}
                    >
                      <SubIcon className="w-4 h-4" />
                      {sub.label}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      );
    }

    return (
      <NavLink key={item.href} to={item.href} end={item.href === '/dashboard'} onClick={() => closeSidebar?.()} className={({ isActive }) => getNavItemClasses(layout, isActive, isColored)} {...(item.tutorialId && { 'data-tutorial': item.tutorialId })}>
        <ThemedIcon className="w-5 h-5" />
        {t(item.nameKey)}
      </NavLink>
    );
  };

  // ═══════════════════════════════════════════════════════════
  // Shared: sous-items « Profil » en liste (dropdowns topnav / rail / mobile)
  // ═══════════════════════════════════════════════════════════
  const renderProfileSubItems = (onNavigate?: () => void, indented = false) =>
    profileSubItems.map((sub) => {
      const SubIcon = resolveNavIcon(iconSet, sub.icon as React.ComponentType<unknown>);
      const isActive = activeProfileKey === sub.key;
      return (
        <div key={sub.key}>
          {sub.separatorBefore && <div className="my-1.5 mx-4 border-t border-gray-200/70" />}
          <button
            onClick={() => { navigate(sub.href); onNavigate?.(); }}
            className={clsx(
              'flex items-center gap-3 w-full py-2.5 text-sm transition-colors text-left',
              indented ? 'pl-11 pr-4' : 'px-4',
              isActive ? 'text-primary-700 bg-primary-50/50' : 'text-gray-600 hover:bg-gray-50'
            )}
            {...(sub.tutorialId && { 'data-tutorial': sub.tutorialId })}
          >
            <SubIcon className="w-4 h-4" />
            {sub.label}
          </button>
        </div>
      );
    });

  // ═══════════════════════════════════════════════════════════
  // Shared: user dropdown (reused across layouts)
  // ═══════════════════════════════════════════════════════════
  const renderUserMenu = () => (
    <>
    <NotificationsMenu buttonClassName={headerStyles.menuButtonClasses} className="mr-1" />
    <div className="relative">
      <button onClick={() => setUserMenuOpen(!userMenuOpen)} className={headerStyles.userMenuButtonClasses}>
        <div className={clsx('w-8 h-8 rounded-full flex items-center justify-center', headerStyles.avatarBg)}>
          <span className={clsx('text-sm', headerStyles.avatarText)}>{user?.firstName?.[0] || user?.email[0].toUpperCase()}</span>
        </div>
        <span className={clsx('hidden md:block text-sm font-medium', headerStyles.nameColor)}>{user?.firstName || user?.email}</span>
        <ChevronDownIcon className={clsx('w-4 h-4', headerStyles.chevronColor)} />
      </button>
      {userMenuOpen && (
        <>
          <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} />
          <div className={headerStyles.dropdownClasses}>
            <NavLink to="/dashboard/profil" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              <UserIcon className="w-4 h-4" />{t('myProfile')}
            </NavLink>
            <div className="border-t my-1" />
            <LanguageSwitcher variant="full" />
            <div className="border-t my-1" />
            <button onClick={handleLogout} className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
              <LogOutIcon className="w-4 h-4" />{t('logout')}
            </button>
          </div>
        </>
      )}
    </div>
    </>
  );

  // ═══════════════════════════════════════════════════════════
  // Shared: mobile drawer (always a full sidebar, for all layouts)
  // ═══════════════════════════════════════════════════════════
  const renderMobileDrawer = () => (
    <div className={clsx('fixed inset-0 z-50 lg:hidden', sidebarOpen ? 'block' : 'hidden')}>
      <div className="fixed inset-0 bg-gray-900/50" onClick={() => setSidebarOpen(false)} />
      <div className={clsx('fixed inset-y-0 left-0 w-64 shadow-xl', sidebarStyles.container)}>
        <div className={clsx('flex items-center justify-between h-16 px-4', sidebarStyles.headerBorder)}>
          <span className={clsx('text-xl font-bold', sidebarStyles.brandColor)}>
            Splinity
            <span className={clsx('text-[10px] font-normal ml-1', sidebarStyles.versionColor)}>({__APP_STAGE__} v{__APP_VERSION__})</span>
          </span>
          <button onClick={() => setSidebarOpen(false)} className={clsx('p-2 rounded-lg', isColored ? 'hover:bg-white/10 text-white/70' : 'hover:bg-gray-100')}>
            <CloseIcon className="w-5 h-5" />
          </button>
        </div>
        <nav className={clsx('p-4', densityStyles.navSpacing)} data-tutorial="main-nav">
          {filteredNavigation.map((item) => renderNavItem(item, () => setSidebarOpen(false)))}
        </nav>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════
  // Shared: modals
  // ═══════════════════════════════════════════════════════════
  const renderModals = () => (
    <>
      <InactivityWarningModal isOpen={showInactivityWarning} secondsRemaining={secondsRemaining} onContinue={extendSession} onLogout={inactivityLogout} />
      <TutorialOverlay />
    </>
  );

  // ═══════════════════════════════════════════════════════════
  // LAYOUT: SIDEBAR (Classic = attached, Aurora = floating)
  // ═══════════════════════════════════════════════════════════
  if (layout.menuStructure === 'sidebar') {
    return (
      <div className="min-h-screen page-bg" style={{ backgroundColor: 'var(--color-bg-base)' }}>
        {renderMobileDrawer()}

        {/* Desktop sidebar — attached (Classic) */}
        {!isFloating && (
          <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
            <div className={clsx('flex flex-col flex-1', sidebarStyles.container)}>
              <div className={clsx('flex items-center h-16 px-6', sidebarStyles.headerBorder)}>
                <span className={clsx('text-xl font-bold', sidebarStyles.brandColor)}>
                  Splinity
                  <span className={clsx('text-[10px] font-normal ml-1', sidebarStyles.versionColor)}>({__APP_STAGE__} v{__APP_VERSION__})</span>
                </span>
              </div>
              <nav className={clsx('flex-1 p-4', densityStyles.navSpacing)} data-tutorial="main-nav">
                {filteredNavigation.map((item) => renderNavItem(item))}
              </nav>
              <div className={clsx('p-4', isColored ? 'border-t border-white/10' : 'border-t')}>
                <button onClick={handleLogout} className={clsx('flex items-center gap-3 w-full px-4 py-2.5 transition-colors', sidebarStyles.logoutClasses)}>
                  <LogOutIcon className="w-5 h-5" />{t('logout')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop sidebar — floating (Aurora): detached panel with rounded corners + shadow */}
        {isFloating && (
          <div className="hidden lg:fixed lg:top-3 lg:left-3 lg:bottom-3 lg:flex lg:w-60 lg:flex-col lg:z-30">
            <div
              className={clsx(
                'flex flex-col flex-1 rounded-2xl overflow-hidden',
                sidebarStyles.container,
              )}
              style={{ boxShadow: '0 8px 32px -4px rgba(30, 10, 60, 0.35), 0 0 0 1px rgba(168, 85, 247, 0.1)' }}
            >
              {/* Brand area — with decorative gradient accent */}
              <div className="relative px-5 pt-6 pb-4">
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-400 via-fuchsia-400 to-pink-400 rounded-t-2xl" />
                <span className={clsx('text-lg font-bold tracking-tight', sidebarStyles.brandColor)}>
                  Splinity
                </span>
                <span className={clsx('block text-[10px] font-normal mt-0.5', sidebarStyles.versionColor)}>
                  {__APP_STAGE__} v{__APP_VERSION__}
                </span>
              </div>

              {/* Navigation — extra padding, pill-shaped items */}
              <nav className={clsx('flex-1 px-3 pb-3', densityStyles.navSpacing)} data-tutorial="main-nav">
                {filteredNavigation.map((item) => renderNavItem(item))}
              </nav>

              {/* Bottom: user avatar + logout */}
              <div className="px-3 pb-4 pt-2 border-t border-white/10">
                <div className="flex items-center gap-3 px-3 py-2 mb-2 rounded-xl bg-white/5">
                  <div className="w-8 h-8 rounded-full bg-white/15 flex items-center justify-center">
                    <span className="text-sm font-medium text-white">{user?.firstName?.[0] || user?.email[0].toUpperCase()}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-white/90 truncate">{user?.firstName || user?.email}</p>
                    <p className="text-[11px] text-white/40 truncate">{user?.email}</p>
                  </div>
                </div>
                <button onClick={handleLogout} className={clsx('flex items-center gap-3 w-full px-4 py-2 transition-colors text-sm', sidebarStyles.logoutClasses)}>
                  <LogOutIcon className="w-4 h-4" />{t('logout')}
                </button>
              </div>
            </div>
          </div>
        )}

        <div className={isFloating ? 'lg:pl-[16.5rem]' : 'lg:pl-64'}>
          <header className={clsx('sticky top-0 z-40 flex items-center justify-between h-16 px-4 lg:px-8', headerStyles.container)}>
            <button onClick={() => setSidebarOpen(true)} className={clsx(headerStyles.menuButtonClasses, 'lg:hidden')}>
              <MenuIcon className="w-5 h-5" />
            </button>
            <div className="flex-1" />
            {renderUserMenu()}
          </header>
          <main className={clsx(densityStyles.mainPadding, densityStyles.mainPaddingDesktop)}>
            <AnnouncementBanner />
            <Outlet />
          </main>
        </div>

        {renderModals()}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LAYOUT: TOPNAV (Breeze)
  // No sidebar on desktop — responsive horizontal nav with overflow → "Plus"
  // ═══════════════════════════════════════════════════════════
  if (layout.menuStructure === 'topnav') {
    return (
      <TopnavLayout
        filteredNavigation={filteredNavigation}
        iconSet={iconSet}
        location={location}
        headerStyles={headerStyles}
        densityStyles={densityStyles}
        moreMenuOpen={moreMenuOpen}
        setMoreMenuOpen={setMoreMenuOpen}
        handleLogout={handleLogout}
        renderUserMenu={renderUserMenu}
        renderModals={renderModals}
        t={t}
        activeProfileKey={activeProfileKey}
        profileFlyoutOpen={profileFlyoutOpen}
        setProfileFlyoutOpen={setProfileFlyoutOpen}
        renderProfileSubItems={renderProfileSubItems}
      />
    );
  }

  // ═══════════════════════════════════════════════════════════
  // LAYOUT: RAIL (Méridien) — shell en GRILLE
  // Le rail est une colonne de grille (z-40) : ancré il élargit la colonne et
  // décale le contenu ; au survol il déborde en surimpression au-dessus de la
  // topbar (z-15), qui reste donc toujours cliquable.
  // ═══════════════════════════════════════════════════════════

  // Sous-items « Réglages » : les trois réglages, puis l'historique du foyer.
  // Le profil personnel, lui, part sur l'avatar.
  const settingsSubItems = [
    ...profileSubItems.filter((s) => s.key.startsWith('settings-')),
    ...profileSubItems.filter((s) => s.key === 'activity'),
  ];

  const railIsActive = (item: NavItem) =>
    item.href === PROFILE_HREF
      ? activeProfileKey !== null && activeProfileKey !== 'personal'
      : item.href === '/dashboard'
        ? location.pathname === '/dashboard'
        : location.pathname.startsWith(item.href);

  // Icônes du prototype, par route
  const railIconFor = (href: string) => {
    switch (href) {
      case '/dashboard': return IconDashboard;
      case '/dashboard/compte-commun': return IconShared;
      case '/dashboard/mon-budget': return IconPersonal;
      case '/dashboard/recherche': return IconStats;
      case '/dashboard/abonnement': return IconEnvelope;
      case '/dashboard/admin': return IconShield;
      case PROFILE_HREF: return IconSettings;
      default: return IconDashboard;
    }
  };

  // Enfants d'une rubrique : comptes réels, ou onglets de réglages
  const railChildrenFor = (item: NavItem): SubNavItem[] | null => {
    if (item.href === '/dashboard/mon-budget' && bankAccounts.length > 0) {
      return bankAccounts.map((a) => ({
        key: `acct-${a.id}`,
        label: a.name,
        href: `/dashboard/mon-budget?bankAccountId=${a.id}`,
        icon: Wallet,
        color: a.color,
        locked: !accessibleBankAccountIds.has(a.id),
      }));
    }
    if (item.href === '/dashboard/compte-commun' && sharedBankAccounts.length > 0) {
      return sharedBankAccounts.map((a) => ({
        key: `sacct-${a.id}`,
        label: a.name,
        href: `/dashboard/compte-commun?sharedBankAccountId=${a.id}`,
        icon: Users,
        color: a.color,
        locked: !accessibleSharedAccountIds.has(a.id),
      }));
    }
    if (item.href === PROFILE_HREF) return settingsSubItems;
    return null;
  };

  const railChildIsActive = (child: SubNavItem) =>
    settingsSubItems.includes(child)
      ? activeProfileKey === child.key
      : location.pathname + location.search === child.href;

  // `onNavigate` : refermer le tiroir tactile une fois la destination atteinte
  const renderRailItem = (item: NavItem, onNavigate?: () => void) => {
    const Icon = railIconFor(item.href);
    const children = railChildrenFor(item);
    const isActive = railIsActive(item);
    const inSub = isActive && !!children?.some(railChildIsActive);
    const open = railOpenGroup === item.href || isActive;
    const label = item.href === PROFILE_HREF ? t('nav.settings') : t(item.nameKey);
    const defaultHref =
      item.href === '/dashboard/mon-budget' && defaultBankAccount
        ? `/dashboard/mon-budget?bankAccountId=${defaultBankAccount.id}`
        : item.href === '/dashboard/compte-commun' && defaultSharedAccount
          ? `/dashboard/compte-commun?sharedBankAccountId=${defaultSharedAccount.id}`
          : item.href === PROFILE_HREF
            ? `${SETTINGS_HREF}?tab=shared`
            : item.href;

    // Dans le rail, la rubrique « Profil » est rendue en « Réglages » (le profil a
    // son propre bouton avatar plus bas) : l'ancre du tutoriel suit le libellé,
    // sinon l'étape « ton profil » pointerait les réglages.
    const railTutorialId = item.href === PROFILE_HREF ? undefined : item.tutorialId;

    return (
      <div key={item.href}>
        {/* L'ancre du tutoriel est portée par le bouton lui-même : l'étape simule
            un clic dessus, un conteneur ne déclencherait aucune navigation. */}
        <button
          type="button"
          onClick={() => { navigate(defaultHref); setRailOpenGroup(item.href); onNavigate?.(); }}
          title={label}
          aria-current={isActive && !inSub ? 'page' : undefined}
          className={clsx('rail-item', isActive && !inSub && 'active', inSub && 'trail')}
          {...(railTutorialId && { 'data-tutorial': railTutorialId })}
        >
          <span className="ri-ico">
            <Icon size={21} />
            {inSub && <span className="ri-subdot" />}
          </span>
          <span className="ri-label">{label}</span>
          {children && (
            <span
              className="ri-chev"
              role="button"
              tabIndex={-1}
              aria-expanded={open}
              onClick={(e) => { e.stopPropagation(); setRailOpenGroup(open ? '' : item.href); }}
            >
              <IconChevron size={15} />
            </span>
          )}
        </button>
        {children && (
          <div className={clsx('rail-sub', open && 'open')}>
            {children.map((child) => {
              const active = railChildIsActive(child);
              return (
                <button
                  key={child.key}
                  type="button"
                  disabled={child.locked}
                  title={child.locked ? t('nav.accountLockedPersonal') : child.label}
                  onClick={() => { if (!child.locked) { navigate(child.href); onNavigate?.(); } }}
                  className={clsx('rail-subitem', active && 'active', child.locked && 'opacity-50 cursor-not-allowed')}
                  {...(child.tutorialId && { 'data-tutorial': child.tutorialId })}
                >
                  {child.color && <span className="rs-dot" style={{ backgroundColor: child.color }} />}
                  <span className="rs-label">{child.label}</span>
                  {active && <span className="rs-mark" />}
                </button>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  const topRailItems = filteredNavigation.filter((i) => i.href !== PROFILE_HREF);
  const settingsNavItem = filteredNavigation.find((i) => i.href === PROFILE_HREF);
  // L'historique est sur la page Profil mais rangé sous « Réglages » : l'avatar
  // ne s'allume que pour le profil personnel.
  const isProfilePage = activeProfileKey === 'personal';

  // Bascule de mode : clair → sombre → système
  const nextMode = mode === 'light' ? 'dark' : mode === 'dark' ? 'system' : 'light';
  const ModeIcon = mode === 'light' ? IconSun : mode === 'dark' ? IconMoon : IconMonitor;

  // Corps de navigation partagé entre le rail (desktop) et le tiroir (tactile)
  const renderRailNav = (onNavigate?: () => void) => (
    <>
      <nav className="rail-nav">{topRailItems.map((item) => renderRailItem(item, onNavigate))}</nav>

      <div className="rail-spacer" />

      {/* Bas : Réglages, puis le profil (avatar) */}
      <div className="rail-nav">
        {settingsNavItem && renderRailItem(settingsNavItem, onNavigate)}
        <button
          type="button"
          onClick={() => { navigate(`${PROFILE_HREF}?tab=personal`); onNavigate?.(); }}
          title={t('myProfile')}
          aria-current={isProfilePage ? 'page' : undefined}
          className={clsx('rail-item', isProfilePage && 'active')}
          data-tutorial="nav-profil"
        >
          <span className="ri-ico">
            <span className="rail-ava">{user?.firstName?.[0] || user?.email[0].toUpperCase()}</span>
          </span>
          <span className="ri-label">{user?.firstName || t('myProfile')}</span>
        </button>
        <button type="button" onClick={handleLogout} title={t('logout')} className="rail-item">
          <span className="ri-ico"><IconLogout size={21} /></span>
          <span className="ri-label">{t('logout')}</span>
        </button>
      </div>
    </>
  );

  const closeRailDrawer = () => setSidebarOpen(false);

  return (
    // Pendant le tutoriel, le rail reste déplié : ses libellés font partie de ce
    // qu'on présente, et une étape qui surligne une icône seule est illisible.
    <div className={clsx('app-shell page-bg', (railPinned || tutorialIsActive) && 'pinned')} style={{ backgroundColor: 'var(--color-bg-base)' }}>
      {/* ─── Tiroir tactile : le rail lui-même, en surimpression (< 1024px) ─── */}
      {sidebarOpen && (
        <>
          <div className="rail-scrim" onClick={closeRailDrawer} aria-hidden />
          <aside className="rail-drawer" role="dialog" aria-modal="true" aria-label={t('nav.menu')} data-tutorial="main-nav">
            <div className="rail-head">
              <span className="rail-brand" title="Splinity">
                <BrandMark />
              </span>
              <span className="rail-wordmark">
                Splinity<span className="rail-ver">{__APP_STAGE__} v{__APP_VERSION__}</span>
              </span>
              {/* Pas d'épingle ici : le tiroir est déjà ouvert en grand */}
              <button type="button" className="rail-pin" onClick={closeRailDrawer} title={t('nav.closeMenu')}>
                <CloseIcon className="w-[17px] h-[17px]" />
              </button>
            </div>
            {renderRailNav(closeRailDrawer)}
          </aside>
        </>
      )}

      {/* ─── Rail ─── */}
      <aside className="rail" data-tutorial="main-nav">
        <div className="rail-inner">
          <div className="rail-head">
            <span className="rail-brand" title="Splinity">
              <BrandMark />
            </span>
            {/* Mot-symbole révélé à l'ouverture → aucun doublon avec la topbar */}
            <span className="rail-wordmark">
              Splinity<span className="rail-ver">{__APP_STAGE__} v{__APP_VERSION__}</span>
            </span>
            <button
              type="button"
              className="rail-pin"
              onClick={toggleRailPin}
              aria-pressed={railPinned}
              title={railPinned ? t('nav.railUnpin') : t('nav.railPin')}
            >
              <IconPin size={17} filled={railPinned} />
            </button>
          </div>

          {renderRailNav()}
        </div>
      </aside>

      {/* ─── Contenu ─── */}
      <div className="min-w-0">
        <div className="topstack">
          <header className="topbar">
            <button onClick={() => setSidebarOpen(true)} className="icon-btn lg:hidden" title={t('nav.openMenu')}>
              <IconMenu size={18} />
            </button>
            {/* Titre de page + contexte — plus de « Splinity / » redondant */}
            <div className="top-title">
              <b>{breadcrumbLabels[0] ?? t('nav.dashboard')}</b>
              {breadcrumbLabels[1] && <span>{breadcrumbLabels[1]}</span>}
            </div>
            <div className="flex-1" />
            {/* Contrôles injectés par la page (période, comptes…) */}
            <div id={TOPBAR_SLOT_ID} className="topbar-slot flex items-center gap-3" />
            <NotificationsMenu buttonClassName="icon-btn" />
            <button
              type="button"
              className="icon-btn"
              onClick={() => setMode(nextMode)}
              title={t(`settings:appearance.mode.${mode}`)}
              aria-label={t('settings:appearance.mode.title')}
            >
              <ModeIcon size={17} />
            </button>
          </header>
        </div>

        <main className="mx-auto w-full" style={{ maxWidth: 1320, padding: '0 34px 60px' }}>
          <AnnouncementBanner />
          <Outlet />
        </main>
      </div>

      {renderModals()}
    </div>
  );
}
